import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BookOpen, Plus, Search, Edit, Trash2, Eye, X,
  ChevronRight, ChevronDown, DollarSign, TrendingUp,
  TrendingDown, Building2, Wallet, PiggyBank
} from 'lucide-react';

interface Account {
  id: number;
  nameEN: string;
  nameAR: string;
  code: string;
  level: number;
  balance: number;
  parentId?: number;
  statusId: number;
  creationDate: string;
  children?: Account[];
}

interface Stats {
  total: number;
  level1: number;
  level2: number;
  level3: number;
  totalBalance: number;
  assets: number;
  liabilities: number;
  equity: number;
  revenue: number;
  expenses: number;
}

export default function AccountsChartPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tree, setTree] = useState<Account[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [form, setForm] = useState({
    nameEN: '', nameAR: '', code: '', level: '1',
    balance: '0', parentId: '', statusId: '1'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accRes, treeRes, statsRes] = await Promise.all([
        api.get('/api/chartofaccounts'),
        api.get('/api/chartofaccounts/tree'),
        api.get('/api/chartofaccounts/stats')
      ]);

      if (accRes.data?.success) {
        setAccounts(Array.isArray(accRes.data.data) ? accRes.data.data : []);
      }
      if (treeRes.data?.success) {
        setTree(Array.isArray(treeRes.data.data) ? treeRes.data.data : []);
        // *H3J9 'D9B/ 'D1&J3J) *DB'&J'K
        const rootIds = new Set<number>();
        treeRes.data.data.forEach((a: Account) => rootIds.add(a.id));
        setExpandedNodes(rootIds);
      }
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (parentId?: number, parentLevel?: number) => {
    setEditingAccount(null);
    setForm({
      nameEN: '', nameAR: '', code: '',
      level: parentLevel ? (parentLevel + 1).toString() : '1',
      balance: '0',
      parentId: parentId ? parentId.toString() : '',
      statusId: '1'
    });
    setShowModal(true);
  };

  const openEditModal = (account: Account) => {
    setEditingAccount(account);
    setForm({
      nameEN: account.nameEN || '',
      nameAR: account.nameAR || '',
      code: account.code || '',
      level: account.level.toString(),
      balance: account.balance.toString(),
      parentId: account.parentId ? account.parentId.toString() : '',
      statusId: account.statusId.toString()
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        nameEN: form.nameEN,
        nameAR: form.nameAR,
        code: form.code,
        level: parseInt(form.level) || 1,
        balance: parseFloat(form.balance) || 0,
        parentId: form.parentId ? parseInt(form.parentId) : null,
        statusId: parseInt(form.statusId) || 1
      };

      if (editingAccount) {
        await api.put('/api/chartofaccounts/' + editingAccount.id, payload);
        alert("تم تحديث الحساب بنجاح");
      } else {
        await api.post('/api/chartofaccounts', payload);
        alert("تم إضافة الحساب بنجاح");
      }
      setShowModal(false);
      loadData();
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف الحساب؟")) return;
    try {
      await api.delete('/api/chartofaccounts/' + id);
      loadData();
      alert("تم حذف الحساب بنجاح");
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ");
    }
  };

  const toggleNode = (id: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };

  const getLevelColor = (level: number) => {
    switch (level) {
      case 1: return 'from-orange-500 to-orange-600';
      case 2: return 'from-blue-500 to-blue-600';
      case 3: return 'from-green-500 to-green-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getLevelBadge = (level: number) => {
    switch (level) {
      case 1: return { text: 'رئيسي', color: 'bg-orange-50 text-orange-700 border-orange-200' };
      case 2: return { text: 'فرعي', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 3: return { text: 'تفصيلي', color: 'bg-green-50 text-green-700 border-green-200' };
      default: return { text: 'حساب', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const filteredTree = search
    ? tree.filter(a =>
        a.nameAR.toLowerCase().includes(search.toLowerCase()) ||
        a.nameEN.toLowerCase().includes(search.toLowerCase()) ||
        a.code.includes(search)
      )
    : tree;

  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition";

  const renderTreeNode = (account: Account, depth: number = 0) => {
    const hasChildren = account.children && account.children.length > 0;
    const isExpanded = expandedNodes.has(account.id);
    const levelBadge = getLevelBadge(account.level);

    return (
      <div key={account.id}>
        <div
          className="flex items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition group"
          style={{ paddingRight: `${depth * 24 + 12}px` }}
        >
          <button
            onClick={() => hasChildren && toggleNode(account.id)}
            className={`w-6 h-6 flex items-center justify-center rounded ${hasChildren ? 'hover:bg-gray-200' : 'invisible'}`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            )}
          </button>

          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getLevelColor(account.level)} flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
            {account.code}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900 truncate">{account.nameAR}</p>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${levelBadge.color}`}>
                {levelBadge.text}
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate">{account.nameEN}</p>
          </div>

          <div className="text-left">
            <p className="font-bold text-green-600">{account.balance.toLocaleString()} ج.م</p>
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            {account.level < 3 && (
              <button
                onClick={() => openCreateModal(account.id, account.level)}
                className="p-1.5 hover:bg-green-50 rounded text-gray-500 hover:text-green-600 transition"
                title="إضافة حساب فرعي"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => { setSelectedAccount(account); setShowViewModal(true); }}
              className="p-1.5 hover:bg-orange-50 rounded text-gray-500 hover:text-orange-600 transition"
              title="عرض"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => openEditModal(account)}
              className="p-1.5 hover:bg-blue-50 rounded text-gray-500 hover:text-blue-600 transition"
              title="تعديل"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(account.id)}
              className="p-1.5 hover:bg-red-50 rounded text-gray-500 hover:text-red-600 transition"
              title="حذف"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="border-r-2 border-gray-100 mr-5">
            {account.children!.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <span>شجرة الحسابات</span>
              <p className="text-sm text-gray-500 font-normal mt-1">إدارة الحسابات المالية والمحاسبية</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={() => openCreateModal()}
          className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg"
        >
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة حساب جديد</span>
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-0 shadow-lg hover:shadow-xl transition">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">الأصول</p>
              <p className="text-2xl font-bold text-gray-900">{stats.assets.toLocaleString()} ج.م</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">الالتزامات</p>
              <p className="text-2xl font-bold text-red-700">{stats.liabilities.toLocaleString()} ج.م</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">حقوق الملكية</p>
              <p className="text-2xl font-bold text-purple-700">{stats.equity.toLocaleString()} ج.م</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">الإيرادات</p>
              <p className="text-2xl font-bold text-green-700">{stats.revenue.toLocaleString()} ج.م</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500">المصروفات</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.expenses.toLocaleString()} ج.م</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="بحث باسم الحساب أو الرمز..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setExpandedNodes(new Set(tree.map(a => a.id)))}
                variant="outline"
                size="sm"
              >
                توسيع الكل
              </Button>
              <Button
                onClick={() => setExpandedNodes(new Set())}
                variant="outline"
                size="sm"
              >
                طي الكل
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">جاري تحميل الحسابات...</p>
            </div>
          ) : tree.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد حسابات</h3>
              <p className="text-gray-500">ابدأ بإضافة حساب جديد</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredTree.map(account => renderTreeNode(account))}
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {editingAccount ? 'تعديل الحساب' : 'إضافة حساب جديد'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>الاسم بالعربية *</Label>
                    <Input
                      value={form.nameAR}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, nameAR: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="مثال: الأصول"
                    />
                  </div>
                  <div>
                    <Label>الاسم بالإنجليزية *</Label>
                    <Input
                      value={form.nameEN}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, nameEN: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="Example: Assets"
                    />
                  </div>
                  <div>
                    <Label>رمز الحساب *</Label>
                    <Input
                      value={form.code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, code: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="مثال: 111"
                    />
                  </div>
                  <div>
                    <Label>المستوى *</Label>
                    <select
                      value={form.level}
                      onChange={(e) => setForm({ ...form, level: e.target.value })}
                      className={inputClass}
                      required
                    >
                      <option value="1">مستوى 1 - رئيسي</option>
                      <option value="2">مستوى 2 - فرعي</option>
                      <option value="3">مستوى 3 - تفصيلي</option>
                    </select>
                  </div>
                  <div>
                    <Label>الرصيد</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.balance}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, balance: e.target.value })}
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>الحساب الأب</Label>
                    <select
                      value={form.parentId}
                      onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">بدون أب (حساب رئيسي)</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.nameAR}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                  <Button type="submit" className="flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                    {editingAccount ? 'تحديث' : 'إضافة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">تفاصيل الحساب</h2>
                <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getLevelColor(selectedAccount.level)} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                    {selectedAccount.code}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedAccount.nameAR}</h3>
                    <p className="text-sm text-gray-500">{selectedAccount.nameEN}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">رمز الحساب</p>
                    <p className="font-semibold font-mono">{selectedAccount.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">المستوى</p>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getLevelBadge(selectedAccount.level).color}`}>
                      {getLevelBadge(selectedAccount.level).text}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">الرصيد</p>
                    <p className="font-semibold text-green-600 text-2xl">{selectedAccount.balance.toLocaleString()} ج.م</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">تاريخ الإنشاء</p>
                    <p className="font-semibold">{new Date(selectedAccount.creationDate).toLocaleDateString('ar-EG')}</p>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedAccount); }} className="flex-1 bg-orange-500 hover:bg-orange-600">
                    <Edit className="w-4 h-4 ml-2" />
                    تعديل الحساب
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
